import { BaseApiService, API_CONFIG } from './base.service';
import {
  UserServerAssignment,
  UserCompanyAssignment,
  UserControllerAssignment,
  Role
} from '../../types';

/**
 * Service for managing user assignments (server, company, controller)
 */
export class UserAssignmentService extends BaseApiService {
  
  // Server Assignments
  async assignUserToServer(assignment: {
    user_id: number;
    server_id: number;
    role_id: number;
    expires_at?: string;
    notes?: string;
  }): Promise<UserServerAssignment> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/server`, assignment);
  }

  async getUserServerAssignments(userId: number): Promise<UserServerAssignment[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/users/${userId}/servers`);
  }

  async removeUserServerAssignment(userId: number, serverId: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/server/${userId}/${serverId}`);
  }

  // Company Assignments
  async assignUserToCompany(assignment: {
    user_id: number;
    company_id: number;
    role_id: number;
    expires_at?: string;
    notes?: string;
  }): Promise<UserCompanyAssignment> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/company`, assignment);
  }

  async getUserCompanyAssignments(userId: number): Promise<UserCompanyAssignment[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/users/${userId}/companies`);
  }

  async removeUserCompanyAssignment(userId: number, companyId: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/company/${userId}/${companyId}`);
  }

  // Controller Assignments
  async assignUserToController(assignment: {
    user_id: number;
    controller_id: number;
    expires_at?: string;
    notes?: string;
  }): Promise<UserControllerAssignment> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/controller`, assignment);
  }

  async getUserControllerAssignments(userId: number): Promise<UserControllerAssignment[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/users/${userId}/controllers`);
  }

  async removeUserControllerAssignment(userId: number, controllerId: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/controller/${userId}/${controllerId}`);
  }

  // Bulk Operations
  async bulkAssignUsers(assignment: {
    user_ids: number[];
    server_ids?: number[];
    company_ids?: number[];
    controller_ids?: number[];
    role_id?: number;
    expires_at?: string;
    notes?: string;
  }): Promise<{ success: number; failed: number; errors: any[] }> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/assignments/bulk`, assignment);
  }

  // User Access Summary
  async getUserAccessSummary(userId: number): Promise<{
    user_id: number;
    servers: Array<{ server_id: number; server_name: string; role: string; is_active: boolean }>;
    companies: Array<{ company_id: number; company_name: string; role: string; is_active: boolean }>;
    controllers: Array<{ controller_id: number; controller_name: string; is_active: boolean }>;
    roles: string[];
    permissions: string[];
  }> {
    return this.get(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/users/${userId}/access`);
  }

  // Permission Checking
  async checkUserPermission(check: {
    user_id: number;
    permission: string;
    server_id?: number;
    company_id?: number;
    controller_id?: number;
  }): Promise<{
    has_permission: boolean;
    user_id: number;
    permission: string;
    context?: any;
  }> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/permissions/check`, check);
  }

  // Roles
  async getRoles(): Promise<Role[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/roles`);
  }

  async createRole(role: {
    name: string;
    display_name: string;
    description?: string;
    is_system_role?: boolean;
  }): Promise<Role> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/roles`, role);
  }

  async updateRole(roleId: number, role: {
    name?: string;
    display_name?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<Role> {
    return this.put(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/roles/${roleId}`, role);
  }

  async deleteRole(roleId: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/roles/${roleId}`);
  }

  // System Initialization
  async initializeDefaultRoles(): Promise<Role[]> {
    return this.post(`${API_CONFIG.ENDPOINTS.USER_MANAGEMENT}/initialize-roles`, {});
  }
}

export const userAssignmentService = new UserAssignmentService();